/**
 * Which failures are expected (a user or business rule said no) versus bugs.
 *
 * Lives apart from `events.ts` so it can be unit tested without loading the
 * analytics SDKs.
 */

/**
 * Expected validation/business-rule messages. We still publish these as events for
 * PostHog/analytics but do not send them to Rollbar (they are not bugs).
 */
const EXPECTED_ERROR_MESSAGES = new Set([
  "A user with this email address already exists",
  "User with this email already exists",
  "User with this email already exists and has completed registration",
  "Email is already verified",
  "User email is already verified",
  "Invalid email or password",
  "Account is inactive or disabled",
  "No account found with this email",
  "Invalid claim token",
  "Invalid or expired token",
  "Invalid or expired verification token",
  "Invalid or expired invitation",
  "User is not pending approval",
  "No valid areas selected",
  "Some selected areas are not available",
  "A tag with this name already exists",
  "A story already exists for this need",
  "This slug is already taken by another organization.",
  "Tax receipts already exist for this need",
  "Receipt has already been emailed",
  "Already on this plan",
  "Cannot upgrade to free plan",
  "No active subscription to upgrade",
  "Only pending offers can be approved",
  "Offer is not available for matching",
  "Need is not open for claims",
  "This need is no longer available for claiming",
  "This claim is not currently active",
  "Email and password are required",
  "Selected areas must belong to the same organization",
  "Areas do not belong to the specified organization",
  "Primary area must be one of the selected service areas",
  "Please check your form data and try again.",
  "No file provided",
  "File size too large. Maximum size is 5MB.",
  "Invalid plan",
  "Price not found for plan",
  "Price not found for new plan",
  "Need must be open or claimed to add a manual claim",
  "Tax-exempt settings are incomplete",
  "Email verification required for creating needs. Please verify your email address first.",
  // AWS SES validation (malformed recipient, e.g. address + trailing space/text)
  "Domain contains control or whitespace",
  // Wrong email or password on the login form (Rollbar #11)
  "Invalid credentials. Please try again.",
  "Organization is not active",
  "Can only create stories for completed needs",
]);

/** Substrings that indicate an expected (non-bug) error; dynamic messages use these. */
const EXPECTED_ERROR_SUBSTRINGS = [
  "already exists in this organization",
  "contains control or whitespace",
  // Expired or used invite link; the page adds "Please contact..." (Rollbar #23)
  "Invalid or expired invitation",
  // Org hit its plan's need limit (Rollbar #14)
  "reached its plan limit",
  // Someone else claimed first while the form was open (Rollbar #44)
  "than available. Only ",
  "Availability changed while you were filling out the form",
];

/**
 * The browser lost the connection mid-request: Safari says "Load failed",
 * Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch
 * resource.", and a dropped React Server Components stream "Connection
 * closed.". Nothing in our code can fix these (Rollbar #60, #61, #65).
 */
const NETWORK_ERROR_MESSAGES = new Set([
  "Load failed",
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Connection closed.",
]);

export function isNetworkErrorMessage(message: string): boolean {
  return NETWORK_ERROR_MESSAGES.has(message);
}

/**
 * A JS chunk from an older deploy is gone, so the tab needs a full reload.
 * Deploy timing, not a bug (Rollbar #68).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  return (
    error.name === "ChunkLoadError" ||
    error.message.includes("Failed to load chunk")
  );
}

/**
 * A bot's injected script replaces the global Error and then fails to
 * serialize a link it grabbed. Every one of Rollbar #74's 13 occurrences had
 * the same headless fingerprint from datacenter IPs and no user clicks; no app
 * code serializes DOM nodes.
 */
const BOT_CIRCULAR_JSON =
  /Converting circular structure to JSON[\s\S]*(HTMLAnchorElement|__reactFiber)/;

function errorText(msg: unknown): string {
  if (msg instanceof Error) return msg.message;

  return typeof msg === "string" ? msg : "";
}

export function isExpectedErrorMessage(data: Record<string, unknown>): boolean {
  const text = errorText(data.error);

  if (!text) return false;

  return (
    EXPECTED_ERROR_MESSAGES.has(text) ||
    isNetworkErrorMessage(text) ||
    isChunkLoadError(data.error) ||
    BOT_CIRCULAR_JSON.test(text) ||
    EXPECTED_ERROR_SUBSTRINGS.some((sub) => text.includes(sub))
  );
}
